// LuckyDayTab.tsx
import React, { useState } from "react";
import { useLaneCopy } from "../../../../utils/laneCopy";
import { Calendar } from "lucide-react";
import AstriaVietnamShell, {
  SHELL_COLORS_ASTRIA_VIETNAM,
} from "../AstriaVietnamShell";
import {
  AstriaVietnamResultCard,
  AstriaVietnamFieldBlock,
  AstriaVietnamLoadingState,
  AstriaVietnamErrorState,
  AstriaVietnamChatCta,
} from "../AstriaVietnamCard";
import {
  WizardStep,
  WizardNav,
  WizardDropdown,
  WizardDateFields,
  WizardDateFieldsValue,
  formatDateFieldsValue,
} from "../AstriaVietnamWizard";
import { useAstriaVietnamTab } from "../useAstriaVietnamTab";
import { AstriaVietnamLuckyDayData } from "../../../../contexts/ChatContext";
import { useAstriaVietnamLanguage } from "../astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "../astriaVietnamCopy";

// SubCategory name must contain "Lucky Day" — matches
// resolveVietnamSubcategoryEntry in
// backend/helper/vietnam/astriaVietnamPromptService.js.
const SUBCATEGORY_NAME = "Lucky Day";

const LuckyDayTab: React.FC = () => {
  const lang = useAstriaVietnamLanguage();
  const copy = useLaneCopy("vn", ASTRIA_VIETNAM_COPY);
  const t = copy.luckyDay;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaVietnamTab({ subCategoryName: SUBCATEGORY_NAME, language: lang });

  const [dateValue, setDateValue] = useState<WizardDateFieldsValue>({
    day: "",
    month: "",
    year: "",
    hourChi: null,
  });
  const [purpose, setPurpose] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const normalizedDate = formatDateFieldsValue(dateValue);
  const step1Valid = !!normalizedDate && !!purpose;

  const handleSubmit = () => {
    send("Check whether this date is a good day for my purpose.", {
      date: normalizedDate || undefined,
      purpose: purpose || undefined,
    });
  };

  const luckyDayData = message?.astriaVietnamData as
    | AstriaVietnamLuckyDayData
    | undefined;

  if (started) {
    return (
      <AstriaVietnamShell active="lucky-day" lang={lang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <AstriaVietnamLoadingState label={t.loading} />}

        {!loading && error && (
          <AstriaVietnamErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && luckyDayData && (
          <>
            <AstriaVietnamResultCard
              icon={<Calendar className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              shareText={`${t.resultTitle}\n\n${luckyDayData.suitability}\n\n${luckyDayData.guidance}`}
            >
              <AstriaVietnamFieldBlock label={t.fields.lunarDate} value={luckyDayData.lunar_date} />
              <AstriaVietnamFieldBlock label={t.fields.suitability} value={luckyDayData.suitability} />
              <AstriaVietnamFieldBlock label={t.fields.bestFor} value={luckyDayData.best_for} />
              <AstriaVietnamFieldBlock label={t.fields.cautionNote} value={luckyDayData.caution_note} />
              <AstriaVietnamFieldBlock label={t.fields.guidance} value={luckyDayData.guidance} />
            </AstriaVietnamResultCard>
            <button
              onClick={() => {
                reset();
                setDateValue({ day: "", month: "", year: "", hourChi: null });
                setPurpose(null);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}
            >
              {shared.startNewReading}
            </button>
            <AstriaVietnamChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !luckyDayData && message && (
          <AstriaVietnamResultCard
            icon={<Calendar className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.75,
                color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaVietnamResultCard>
        )}
      </AstriaVietnamShell>
    );
  }

  return (
    <AstriaVietnamShell active="lucky-day" lang={lang}>
      <WizardStep stepKey="details" question={t.step1Question} helper={t.step1Helper}>
        <div style={{ marginBottom: 18 }}>
          <WizardDateFields
            value={dateValue}
            onChange={setDateValue}
            hourOptions={[]}
            hourLabel=""
            hourPlaceholder=""
            dayPlaceholder={shared.dayPlaceholder}
            monthPlaceholder={shared.monthPlaceholder}
            yearPlaceholder={shared.yearPlaceholder}
            showHour={false}
          />
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            marginBottom: 8,
          }}
        >
          {t.purposeLabel}
        </div>
        <WizardDropdown
          options={t.purposeOptions}
          value={purpose}
          onChange={setPurpose}
          placeholder={t.purposePlaceholder}
        />

        <WizardNav
          showBack={false}
          nextDisabled={!step1Valid}
          onNext={handleSubmit}
          nextLabel={t.revealButton}
          backLabel={shared.back}
        />
      </WizardStep>
    </AstriaVietnamShell>
  );
};

export default LuckyDayTab;
